import { reactive, nextTick } from "vue";
import { message } from "ant-design-vue";
import SequenceService from "@/src/api/SequenceService.js";
import DashboardService from "@/src/api/DashboardService.js";
import EmailService from "@/src/api/EmailService.js";
import {
  createValidationPayload,
  createListPayload,
  createManualPayload,
  createEmailPayload,
} from "@/src/utils/sequenceUtils.js";
import { validationTypes } from "@/src/constants/sequenceConstants.js";
import { featureNames } from "@/src/constants/globalConstants";

export function useSequenceAddition() {
  const state = reactive({
    isLoading: false,
    isProcessingValidation: false,

    configId: null,
    validationResults: null,
    pendingValidationData: null,
    originalValidationParams: null,

    modalStates: {
      listModal: false,
      manualModal: false,
      validationModal: false,
      setupSenderDetailsModal: false,
    },

    forceCloseValidationModal: false,

    selectedListId: null,
    selectedEntities: {
      selected_entities: [],
      unselected_entities: [],
      select_all: false,
    },
  });

  const resetState = () => {
    state.isLoading = false;
    state.isProcessingValidation = false;
    state.configId = null;
    state.validationResults = null;
    state.pendingValidationData = null;
    state.originalValidationParams = null;
    state.selectedEntities = {
      selected_entities: [],
      unselected_entities: [],
      select_all: false,
    };
    state.selectedListId = null;
    state.forceCloseValidationModal = false;

    Object.keys(state.modalStates).forEach((key) => {
      state.modalStates[key] = false;
    });
  };

  const addDirectWithConfig = async (
    sequenceId,
    entities,
    flowType = "manual",
  ) => {
    try {
      state.isLoading = true;

      const entityList = entities.map((entity) => ({
        id: entity.id,
        entity_type:
          entity.entity_type?.toLowerCase() || entity.type?.toLowerCase(),
      }));

      state.pendingValidationData = createValidationPayload(
        validationTypes.MANUAL,
        { sequenceId, entityList },
      );

      state.modalStates.setupSenderDetailsModal = true;

      return { success: true, flow: "direct" };
    } catch (error) {
      console.error("Error in direct flow setup:", error);
      message.error("An error occurred while setting up direct addition");
      return { success: false, error };
    } finally {
      state.isLoading = false;
    }
  };

  const addListWithValidation = async (sequenceId, listId, viewId = null) => {
    try {
      state.isLoading = true;
      state.selectedListId = listId;

      state.pendingValidationData = createValidationPayload(
        validationTypes.LIST,
        { sequenceId, listId, viewId },
      );

      state.modalStates.listModal = false;
      state.modalStates.setupSenderDetailsModal = true;

      return { success: true, flow: "list" };
    } catch (error) {
      console.error("Error in list flow setup:", error);
      message.error("An error occurred while setting up list addition");
      return { success: false, error };
    } finally {
      state.isLoading = false;
    }
  };

  const addManualWithValidation = async (sequenceId, entities) => {
    try {
      state.isLoading = true;

      const entityList = entities.map((entity) => ({
        id: entity.id,
        entity_type:
          entity.entity_type?.toLowerCase() || entity.type?.toLowerCase(),
      }));

      state.pendingValidationData = createValidationPayload(
        validationTypes.MANUAL,
        { sequenceId, entityList },
      );

      state.modalStates.manualModal = false;
      state.modalStates.setupSenderDetailsModal = true;

      return { success: true, flow: "manual" };
    } catch (error) {
      console.error("Error in manual flow setup:", error);
      message.error("An error occurred while setting up manual addition");
      return { success: false, error };
    } finally {
      state.isLoading = false;
    }
  };

  const addEmailWithValidation = async (
    sequenceId,
    entityEmails,
    filterData = {},
  ) => {
    try {
      state.isLoading = true;

      state.pendingValidationData = createValidationPayload(
        validationTypes.EMAIL,
        { sequenceId, entityEmails, filterData },
      );

      state.modalStates.setupSenderDetailsModal = true;

      return { success: true, flow: "email" };
    } catch (error) {
      console.error("Error in email flow setup:", error);
      message.error("An error occurred while setting up email addition");
      return { success: false, error };
    } finally {
      state.isLoading = false;
    }
  };

  const handleSenderSetup = async (setupData, flowType = "validation") => {
    try {
      state.isLoading = true;
      state.isProcessingValidation = true;

      if (setupData?.configId) {
        state.configId = setupData.configId;
      }

      if (!state.pendingValidationData) {
        message.error("No validation data found");
        return { success: false };
      }

      const storedValidationParams = state.pendingValidationData;

      if (flowType === "direct") {
        const response = await addEntitiesDirectly(storedValidationParams);

        if (response.success) {
          state.modalStates.setupSenderDetailsModal = false;
          state.pendingValidationData = null;
          return { success: true, response };
        } else {
          message.error(
            response.message ||
              `Failed to add entities to ${featureNames.SEQUENCES}`,
          );
          return { success: false };
        }
      } else {
        state.modalStates.setupSenderDetailsModal = false;
        state.originalValidationParams = storedValidationParams;

        await nextTick();

        state.validationResults = null;
        state.modalStates.validationModal = true;
        state.pendingValidationData = null;

        try {
          let response;

          if (
            storedValidationParams?.validation_type === validationTypes.EMAIL
          ) {
            const { sequence_id, entity_emails, ...restParams } =
              storedValidationParams;
            const emailPayload = {
              entity_emails: entity_emails,
              page_size: restParams.page_size || 500,
              user_id: restParams.user_id,
            };

            const { validation_type, ...filterData } = restParams;

            response = await EmailService.addEmailsToSequence(
              sequence_id,
              emailPayload,
              filterData,
            );
          } else {
            response = await SequenceService.validateEntities(
              storedValidationParams,
            );
          }

          if (response.success) {
            state.validationResults = response;

            if (
              storedValidationParams?.validation_type === validationTypes.LIST
            ) {
              state.validationResults.list_id = storedValidationParams.list_id;
            }

            return { success: true, validationResults: response };
          } else {
            // Simple force close: trigger flag and reset
            state.forceCloseValidationModal = true;
            state.modalStates.validationModal = false;
            setTimeout(() => state.forceCloseValidationModal = false, 100);
            
            if (response.is_empty_list) {
              message.error(response.message || "List is empty");
            } else {
              message.error("Failed to validate entities");
            }
            return { success: false };
          }
        } catch (error) {
          // Simple force close: trigger flag and reset
          state.forceCloseValidationModal = true;
          state.modalStates.validationModal = false;
          setTimeout(() => state.forceCloseValidationModal = false, 100);
          message.error("An error occurred while validating entities");
          return { success: false, error };
        }
      }
    } catch (error) {
      console.error("Error in sender setup handler:", error);
      message.error("An error occurred while processing sender setup");
      return { success: false, error };
    } finally {
      state.isLoading = false;
      state.isProcessingValidation = false;
    }
  };

  const addEntitiesDirectly = async (validationParams) => {
    try {
      const { sequenceId, entityList } =
        parseValidationParams(validationParams);

      const payload = {
        sequence_entity_config_id: state.configId,
        entity_data: entityList,
      };

      const response = await SequenceService.addDifferentEntitiesToSequence(
        sequenceId,
        payload,
      );

      return {
        success: response?.success || false,
        message: response?.message,
        response,
      };
    } catch (error) {
      console.error("Error in direct addition:", error);
      return {
        success: false,
        message: "An error occurred during direct addition",
        error,
      };
    }
  };

  const handleValidationCompletion = async (genericData) => {
    try {
      const {
        selectedCategories,
        validationResults,
        validationType,
        sequenceId,
      } = genericData;
      let response;

      if (validationType === validationTypes.LIST) {
        const listPayload = createListPayload({
          selectedCategories,
          validationResults,
          sequenceId,
          configId: state.configId,
          viewId: state.originalValidationParams?.view_id || null,
        });

        response = await DashboardService.addListToSequence(listPayload);

        if (response?.success) {
          message.success(
            `List added to the ${featureNames.SEQUENCES} successfully`,
          );
          state.modalStates.validationModal = false;
          state.validationResults = null;
        } else {
          message.error(
            response?.message ||
              `Failed to add list to the ${featureNames.SEQUENCES}`,
          );
        }
      } else if (validationType === validationTypes.EMAIL) {
        const emailPayload = createEmailPayload({
          selectedCategories,
          validationResults,
          configId: state.configId,
        });

        if (
          !emailPayload.entity_data ||
          emailPayload.entity_data.length === 0
        ) {
          message.error("No contact(s)/lead(s) selected to add");
          return { success: false };
        }

        response = await SequenceService.addDifferentEntitiesToSequence(
          sequenceId,
          emailPayload,
        );

        if (response?.success) {
          const entityCount = emailPayload.entity_data.length;
          const entityText =
            entityCount === 1 ? "contact/lead" : "contacts/leads";
          message.success(
            `${entityCount} ${entityText} added to the ${featureNames.SEQUENCES} successfully`,
          );
          state.modalStates.validationModal = false;
          state.validationResults = null;
        } else {
          message.error(
            response?.message ||
              `Failed to add entities to the ${featureNames.SEQUENCES}`,
          );
        }
      } else {
        const manualPayload = createManualPayload({
          selectedCategories,
          validationResults,
          configId: state.configId,
        });

        if (
          !manualPayload.entity_data ||
          manualPayload.entity_data.length === 0
        ) {
          message.error("No contact(s)/lead(s) selected to add");
          return { success: false };
        }

        response = await SequenceService.addDifferentEntitiesToSequence(
          sequenceId,
          manualPayload,
        );

        if (response?.success) {
          const entityCount = manualPayload.entity_data.length;
          const entityText =
            entityCount === 1 ? "contact/lead" : "contacts/leads";
          message.success(
            `${entityCount} ${entityText} added to the ${featureNames.SEQUENCES} successfully`,
          );
          state.modalStates.validationModal = false;
          state.validationResults = null;
        } else {
          message.error(
            response?.message ||
              `Failed to add entities to the ${featureNames.SEQUENCES}`,
          );
        }
      }

      return { success: response?.success || false, response };
    } catch (error) {
      console.error("Error in validation completion:", error);
      message.error(
        `An error occurred while adding entities to the ${featureNames.SEQUENCES}`,
      );
      return { success: false, error };
    }
  };

  const parseValidationParams = (validationParams) => {
    const sequenceId = validationParams.sequence_id;
    const entityList = validationParams.entity_list || [];

    return { sequenceId, entityList };
  };

  const updateValidationResults = (newValidationResults) => {
    state.validationResults = newValidationResults;
  };

  const updateSelectedEntities = (newSelection) => {
    state.selectedEntities = newSelection;
  };

  const updateSelectedListId = (listId) => {
    state.selectedListId = listId;
  };

  const setModalState = (modalName, isVisible) => {
    if (state.modalStates.hasOwnProperty(modalName)) {
      state.modalStates[modalName] = isVisible;
    }
  };

  const handleSetupModalClose = () => {
    if (!state.isProcessingValidation && state.pendingValidationData) {
      state.pendingValidationData = null;
    }
  };

  const setDirectValidationData = (count, validationType = "DIRECT") => {
    state.pendingValidationData = {
      entity_list: new Array(count).fill({}),
      validation_type: validationType,
      sequence_id: null,
    };
  };



  return {
    state,

    addDirectWithConfig,
    addListWithValidation,
    addManualWithValidation,
    addEmailWithValidation,

    handleSenderSetup,
    handleValidationCompletion,
    updateValidationResults,
    updateSelectedEntities,
    updateSelectedListId,
    handleSetupModalClose,
    setDirectValidationData,

    setModalState,
    resetState,
  };
}
